import { useContext } from 'react';
import cookies from 'js-cookie';
import { CommonStore, userStore } from 'stores';
import queryString from 'query-string';

type cookiesKey =
    | 'targetingCookies'
    | 'performanceCookies'
    | 'necessaryCookies';
type onetrustGroupsKey = 'C0004' | 'C0002' | 'C0001';

const onetrustGroupsMapping: { [key in cookiesKey]: string } = {
    targetingCookies: 'C0004',
    performanceCookies: 'C0002',
    necessaryCookies: 'C0001',
};

let optanonCookie = queryString.parse(String(cookies.get('OptanonConsent')));

let onetrustGroups: { [key: string]: number } = {
    C0004: 0,
    C0002: 0,
    C0001: 1,
};

const cookiesSetEvent = new Event('cookiesSet');

const get = (key: cookiesKey) => {
    if (!onetrustGroups) {
        return;
    }
    return !!onetrustGroups[onetrustGroupsMapping[key]];
};

const set = (key: cookiesKey, value: boolean) => {
    if (!onetrustGroups) {
        return;
    }
    onetrustGroups[onetrustGroupsMapping[key]] = Number(value);

    // Update optanon cookie
    const stringifiedOnetrustGroups = Object.entries(onetrustGroups)
        .map(([key, value]) => key + ':' + value)
        .join(',');
    optanonCookie.groups = stringifiedOnetrustGroups;
    document.cookie = `OptanonConsent=${queryString.stringify(
        optanonCookie,
    )};domain=.yousician.com`;

    // Send GTM dataLayer update for OnetrustActiveGroups
    const onetrustActiveGroups = `,${Object.keys(onetrustGroups)
        .filter((key) => onetrustGroups[key as onetrustGroupsKey])
        .join(',')},`;
    (window as any).OnetrustActiveGroups = onetrustActiveGroups;
    const dataLayer = (window as any).dataLayer;
    dataLayer &&
        dataLayer.push({
            OptanonActiveGroups: onetrustActiveGroups,
            OnetrustActiveGroups: onetrustActiveGroups,
        });

    const OptanonWrapper = (window as any).OptanonWrapper;
    typeof OptanonWrapper === 'function' && OptanonWrapper();

    onCookieSet();
};

const update = () => {
    optanonCookie = queryString.parse(String(cookies.get('OptanonConsent')));

    if (!optanonCookie.groups || typeof optanonCookie.groups !== 'string') {
        return;
    }

    onetrustGroups = optanonCookie.groups.split(',').reduce((prev, next) => {
        const key = next.split(':')[0];
        const value = Number(next.split(':')[1]);
        return {
            ...prev,
            [key]: value,
        };
    }, {});
};

const onCookieSet = () => {
    window.dispatchEvent(cookiesSetEvent);
};

export const useOnetrustGroupsUpdate = () => {
    const commonStore = useContext(CommonStore);

    (window as any).addEventListener('cookiesSet', () => {
        sessionStorage.setItem(
            'previous_data_sales',
            commonStore.user.data_sales,
        );
        userStore
            .update({
                field: 'data_sales',
                value: get('targetingCookies') ? 'allowed' : 'denied',
            })
            .then(() => {
                commonStore.retrieveCurrentUser();
            });
    });

    (window as any).onload = () => {
        update();

        if (!commonStore.user) {
            return;
        }

        if (
            commonStore.user.location &&
            commonStore.user.location.region !== 'California'
        ) {
            return;
        }

        onCookieSet();
    };
};

export default {
    get,
    set,
    update,
};
